// Session bindings, per browser tab: one session per ISSUE, and one for chat.
//
// Issue work binds per (project, issue). `ensure()` for issue X hands out only:
//
//   1. X's own session (this tab worked on X before — Back, a reload, a direct
//      URL, a remount);
//   2. the tab's unclaimed PRE-WARM, which X claims on the spot;
//   3. a new session.
//
// It never hands out a session that holds another issue. It used to: the issue
// slot was one binding per project, so every issue after the first landed in
// the first issue's session (dev 8647d326, 2026-09-24: one POST /sessions,
// seven issues, `client.session.bind source=memory|sessionStorage` on each).
// One transcript then held several issues, and when its sandbox was dying the
// next issue's refine request was queued into it, answered by the servant, and
// never reached the rebuilt sandbox (16653841, #183 -> #184).
//
// THE PRE-WARM. A surface that names no issue yet — the Focus picker and the
// create wizard — asks with no issue. It gets the tab's WARM session: minted in
// the background so the sandbox boots while the user chooses, and seeded by
// nobody (only RefineFlow and IssuePage send an issue seed, and they claim it
// first). The warm binding lives in this module's MEMORY ONLY. A duplicated tab
// copies sessionStorage, so a stored warm id would be claimed by one issue in
// each tab — two issues, one session again. The cost is that a reload on the
// picker or the wizard mints a new pre-warm; the page-hide beacon hands the old
// one to the server's detach grace.
//
// Chat holds a slot of its own, one binding per project. A chat and an issue
// conversation are different conversations, and the server's `kind` promotion
// is monotonic per session row, so a single flow turn would re-label the row
// the history rail lists as a chat.
//
// Nothing in this module tears a sandbox down; the surface that leaves a session
// owns its detach (RefineFlow and the issue page's embedded chat detach on
// unmount, so the session of the issue just left enters the detach grace).
//
// Boot-on-interaction: nothing happens until `ensure()` is called. An in-flight
// mint is shared, so two near-simultaneous `ensure()` calls for the same
// binding do not race two `POST /sessions`.

import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import { reportClientEventTo } from "../lib/client-trace";
import { HttpError, httpGet } from "../lib/http";
import { detachSession } from "../lib/sessionBeacon";
import { createSession } from "../projects/useProjects";

/**
 * Which conversation of a project a binding is for. `issue` is the default;
 * with an issue number it is that issue's own binding, without one it is the
 * tab's pre-warm.
 */
export type SessionSlot = "issue" | "chat";

type Binding =
  | { readonly kind: "chat" }
  | { readonly kind: "issue"; readonly iid: number }
  | { readonly kind: "warm" };

const bindingOf = (slot: SessionSlot, issueIid?: number | null): Binding =>
  slot === "chat"
    ? { kind: "chat" }
    : typeof issueIid === "number"
      ? { kind: "issue", iid: issueIid }
      : { kind: "warm" };

const WARM: Binding = { kind: "warm" };

/** The module-map key. The cache, the in-flight map, the origin map and the
 *  generation counter all share it. */
const memKey = (projectId: number, b: Binding): string =>
  b.kind === "chat"
    ? `chat:${projectId}`
    : b.kind === "issue"
      ? `issue:${projectId}:${b.iid}`
      : `issue:${projectId}:warm`;

// Per-TAB durable bindings. The module cache dies on a full page reload; this
// sessionStorage mirror survives it, so a reload re-adopts the SAME session
// (paired with the server's detach grace) instead of cold-booting a pipeline.
// Per-tab on purpose: two tabs are two independent contexts.
const CHAT_BINDING_PREFIX = "loom.session:chat:";
const ISSUE_BINDING_PREFIX = "loom.issueSession:";
/** The one binding the bundle before per-issue sessions wrote for ALL issue
 *  work of a project. Read once, then removed (`migrateLegacy`). */
const LEGACY_ISSUE_PREFIX = "loom.session:";

/** `null` for the pre-warm, which is never persisted (see the header). */
const storageKeyOf = (projectId: number, b: Binding): string | null =>
  b.kind === "chat"
    ? CHAT_BINDING_PREFIX + projectId
    : b.kind === "issue"
      ? `${ISSUE_BINDING_PREFIX}${projectId}:${b.iid}`
      : null;

const readStorage = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeStorage = (key: string, id: string): void => {
  try {
    window.sessionStorage.setItem(key, id);
  } catch {
    // Private mode / quota — the in-memory cache still serves this tab; we just
    // lose the reload-survival, degrading to the prior cold-boot behaviour.
  }
};
const removeStorage = (key: string): void => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const sessionCache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

/**
 * Where each in-memory binding came from — read ONLY by the `session.bind`
 * trace. A memory hit alone cannot say it: the first read after a reload copies
 * the sessionStorage mirror into memory, so by the time `ensure()` runs every
 * reload-adopted id "is in memory".
 */
type BindingOrigin = "sessionStorage" | "minted" | "adopted" | "claimed" | "legacy";
const bindingOrigin = new Map<string, BindingOrigin>();

// Generation of each binding — the compare-and-swap a mint settles against.
//
// `POST /sessions` waits on GitLab round trips (membership pre-flight, the
// `.loom.yml` fetch, the project fetch), so the window between "mint started"
// and "mint resolved" is hundreds of ms to seconds. A binding move inside that
// window — a history pick's `adoptProjectSession`, or `reset()` — is the user's
// choice and must win: the mint captures the generation when it starts and
// writes the binding only if the generation is unchanged when it lands. When it
// moved, the minted session belongs to nobody, so it is detached rather than
// left to burn its own budget window.
const bindingGeneration = new Map<string, number>();
const generationOf = (key: string): number => bindingGeneration.get(key) ?? 0;
const bumpGeneration = (key: string): void => {
  bindingGeneration.set(key, generationOf(key) + 1);
};

/** The binding's id: memory, or — after a reload wiped memory — the per-tab
 *  mirror, which is re-seeded into memory so `ensure()` ADOPTS it. */
const readBinding = (
  projectId: number,
  b: Binding,
): { readonly id: string; readonly source: "memory" | "sessionStorage" } | null => {
  const key = memKey(projectId, b);
  const inMemory = sessionCache.get(key);
  if (inMemory !== undefined) return { id: inMemory, source: "memory" };
  const storageKey = storageKeyOf(projectId, b);
  const persisted = storageKey === null ? null : readStorage(storageKey);
  if (persisted !== null) {
    sessionCache.set(key, persisted);
    bindingOrigin.set(key, "sessionStorage");
    return { id: persisted, source: "sessionStorage" };
  }
  return null;
};

const writeBinding = (
  projectId: number,
  b: Binding,
  id: string,
  origin: BindingOrigin,
): void => {
  const key = memKey(projectId, b);
  sessionCache.set(key, id);
  bindingOrigin.set(key, origin);
  const storageKey = storageKeyOf(projectId, b);
  if (storageKey !== null) writeStorage(storageKey, id);
};

/** Forget a binding in memory and storage, and stand down its in-flight mint. */
const dropBinding = (projectId: number, b: Binding): void => {
  const key = memKey(projectId, b);
  sessionCache.delete(key);
  bindingOrigin.delete(key);
  inflight.delete(key);
  bumpGeneration(key);
  const storageKey = storageKeyOf(projectId, b);
  if (storageKey !== null) removeStorage(storageKey);
};

/**
 * Move the pre-warm under issue `iid`, if it is still `expected` (or any id,
 * when `expected` is omitted). Returns the claimed id, or null when there was
 * nothing to claim. The pre-warm is gone afterwards, so no other issue can
 * ever be handed the same id.
 */
const claimWarm = (
  projectId: number,
  iid: number,
  expected?: string,
): string | null => {
  const warmKey = memKey(projectId, WARM);
  const id = sessionCache.get(warmKey);
  if (id === undefined || (expected !== undefined && id !== expected)) return null;
  sessionCache.delete(warmKey);
  bindingOrigin.delete(warmKey);
  writeBinding(projectId, { kind: "issue", iid }, id, "claimed");
  return id;
};

// ── The binding the bundle before this one left in the tab ────────────────
//
// `loom.session:<projectId>` held ONE id for all issue work, so its session may
// hold any issue — or several. It is never handed out on sight. The server's
// derived flow (`GET /sessions/:id` → `flowIssueIid`, the value
// `reconnect.reuse` logs) decides: the id is filed under the issue it is on, so
// a reload mid-refine and Back to that issue still find it, and every other
// issue gets a session of its own. An ended session, one with no flow, and a
// failed read are dropped. One-time loss, by design: an EARLIER issue that
// session also held (the "parked" one) can no longer be reached from this tab,
// and reopening it starts a new session.
const LegacyDetailSchema = z.object({
  endedAt: z.string().nullable().optional(),
  flowIssueIid: z.number().nullable().optional(),
});
const legacyMigrations = new Map<number, Promise<void>>();

const legacyStorageKey = (projectId: number): string =>
  LEGACY_ISSUE_PREFIX + projectId;

const hasLegacyWork = (projectId: number): boolean =>
  legacyMigrations.has(projectId) ||
  readStorage(legacyStorageKey(projectId)) !== null;

const migrateLegacy = (projectId: number): Promise<void> => {
  const running = legacyMigrations.get(projectId);
  if (running !== undefined) return running;
  const legacy = readStorage(legacyStorageKey(projectId));
  if (legacy === null) return Promise.resolve();
  removeStorage(legacyStorageKey(projectId));
  const run = httpGet(`/sessions/${encodeURIComponent(legacy)}`, LegacyDetailSchema)
    .then((d) => {
      if (typeof d.endedAt === "string") return;
      if (typeof d.flowIssueIid !== "number") return;
      const owner: Binding = { kind: "issue", iid: d.flowIssueIid };
      if (readBinding(projectId, owner) === null) {
        writeBinding(projectId, owner, legacy, "legacy");
      }
    })
    .catch(() => undefined)
    .finally(() => {
      legacyMigrations.delete(projectId);
    });
  legacyMigrations.set(projectId, run);
  return run;
};

// ── Trace ──────────────────────────────────────────────────────────────────

type BindSource =
  | "memory"
  | "sessionStorage"
  | "adopted"
  | "claimed"
  | "legacy"
  | "inflight"
  | "minted";

/**
 * Report which session `ensure()` handed a surface, and from where, onto THAT
 * session's own trace (`jsonPayload.event="client.session.bind"`):
 *
 *   - `memory`         a session THIS page bound to this binding earlier,
 *   - `sessionStorage` the tab's mirror — the binding survived a reload,
 *   - `adopted`        a session the user picked (history rail),
 *   - `claimed`        the tab's pre-warm, taken by the issue that asked,
 *   - `legacy`         the older bundle's one binding, filed under the issue
 *                      the server says it holds,
 *   - `minted`         a new session (`POST /sessions`),
 *   - `inflight`       a join of a mint another surface started.
 *
 * `issueIid` is the issue the surface asked for; absent for the pre-warm and
 * for chat.
 */
const traceBind = (
  sessionId: string,
  source: BindSource,
  projectId: number,
  slot: SessionSlot,
  b: Binding,
): void => {
  reportClientEventTo(sessionId, "session.bind", {
    source,
    slot,
    projectId,
    ...(b.kind === "issue" ? { issueIid: b.iid } : {}),
  });
};

/** The trace source for a binding hit: where that binding originally came from. */
const hitSource = (
  key: string,
  loadedNow: "memory" | "sessionStorage",
): BindSource => {
  if (loadedNow === "sessionStorage") return "sessionStorage";
  const origin = bindingOrigin.get(key);
  return origin === undefined || origin === "minted" ? "memory" : origin;
};

// ── Resolution ─────────────────────────────────────────────────────────────

interface Resolved {
  readonly id: string;
  readonly source: BindSource;
}

/** What can be handed out without a request: the binding's own id, or — for an
 *  issue — the pre-warm, claimed on the spot. */
const resolveNow = (projectId: number, b: Binding): Resolved | null => {
  const own = readBinding(projectId, b);
  if (own !== null) {
    return { id: own.id, source: hitSource(memKey(projectId, b), own.source) };
  }
  if (b.kind === "issue") {
    const claimed = claimWarm(projectId, b.iid);
    if (claimed !== null) return { id: claimed, source: "claimed" };
  }
  return null;
};

/** Start (or return) the mint for this binding. SYNCHRONOUS up to the request,
 *  so a second `ensure()` in the same tick finds it in `inflight` and joins. */
const mintInto = (
  projectId: number,
  b: Binding,
  generation: number,
): Promise<string> => {
  const key = memKey(projectId, b);
  const running = inflight.get(key);
  if (running !== undefined) return running;
  const promise: Promise<string> = createSession(projectId)
    .then(({ sessionId: id }) => {
      if (inflight.get(key) === promise) inflight.delete(key);
      if (generationOf(key) !== generation) {
        // A pick (or a reset) moved the binding while this was in flight. The
        // freshly minted session is bound to nothing and no surface will ever
        // attach to it, so hand it to the detach grace.
        detachSession(id);
        return id;
      }
      // Persisted (except for the pre-warm) so a reload re-adopts this id
      // instead of cold-booting.
      writeBinding(projectId, b, id, "minted");
      return id;
    })
    .catch((err: unknown) => {
      if (inflight.get(key) === promise) inflight.delete(key);
      throw err;
    });
  inflight.set(key, promise);
  return promise;
};

/**
 * Everything past `resolveNow`, in order: a mint already running for this
 * binding is joined; an issue joins the pre-warm's mint and claims it when it
 * lands (or, when another surface claimed it first, starts over); otherwise a
 * new session is minted for this binding.
 */
const settle = (
  projectId: number,
  b: Binding,
  generation: number,
): Promise<Resolved> => {
  const key = memKey(projectId, b);
  const now = resolveNow(projectId, b);
  if (now !== null) return Promise.resolve(now);
  const own = inflight.get(key);
  if (own !== undefined) return own.then((id) => ({ id, source: "inflight" }));
  if (b.kind === "issue") {
    const warmFlight = inflight.get(memKey(projectId, WARM));
    if (warmFlight !== undefined) {
      return warmFlight.then((id) => {
        // Stood down (reset / adopt of this issue): the caller discards it.
        if (generationOf(key) !== generation) return { id, source: "inflight" };
        // Bound while this waited (another mount of the same issue): use that
        // binding rather than claiming over it.
        if (readBinding(projectId, b) !== null) return settle(projectId, b, generation);
        if (claimWarm(projectId, b.iid, id) !== null) {
          return { id, source: "inflight" };
        }
        return settle(projectId, b, generation);
      });
    }
  }
  return mintInto(projectId, b, generation).then((id) => ({
    id,
    source: "minted",
  }));
};

/** The id a surface may render BEFORE `ensure()`: its own binding, else — for
 *  an issue — the pre-warm it is about to claim. Never the older bundle's
 *  binding, which needs the server's answer first. */
const initialId = (projectId: number, b: Binding): string | null =>
  readBinding(projectId, b)?.id ??
  (b.kind === "issue" ? (sessionCache.get(memKey(projectId, WARM)) ?? null) : null);

// ── Exports ────────────────────────────────────────────────────────────────

/**
 * Forget the per-tab mirror of one binding. Called when the bound session is
 * known dead (expired/terminated) so the next reload cold-boots a fresh one
 * rather than re-adopting the corpse (the expiry-recovery trap). Leaves the
 * in-memory binding, so the mount that observed the death keeps rendering it.
 */
export const clearPersistedSession = (
  projectId: number,
  slot: SessionSlot = "issue",
  issueIid?: number | null,
): void => {
  const storageKey = storageKeyOf(projectId, bindingOf(slot, issueIid));
  if (storageKey !== null) removeStorage(storageKey);
};

/**
 * The session id bound in this tab — the in-memory cache, or the sessionStorage
 * mirror a reload left behind. Read-only twin of what `useProjectSession`
 * initialises its state from, for callers that need the id OUTSIDE a render.
 * For the issue slot it is that issue's own binding, or the pre-warm when no
 * issue is named. `null` when there is none.
 */
export const readProjectSessionId = (
  projectId: number,
  slot: SessionSlot = "issue",
  issueIid?: number | null,
): string | null => readBinding(projectId, bindingOf(slot, issueIid))?.id ?? null;

/**
 * Is this session bound to ISSUE work anywhere in this tab — the pre-warm, any
 * issue's own binding, or the older bundle's one binding not yet migrated? The
 * history rail refuses such a row, so a session issue work stands in can never
 * also become the chat's.
 */
export const isIssueWorkSession = (
  projectId: number,
  sessionId: string,
): boolean => {
  const prefix = `issue:${projectId}:`;
  for (const [key, id] of sessionCache) {
    if (key.startsWith(prefix) && id === sessionId) return true;
  }
  if (readStorage(legacyStorageKey(projectId)) === sessionId) return true;
  const storagePrefix = `${ISSUE_BINDING_PREFIX}${projectId}:`;
  try {
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (
        key !== null &&
        key.startsWith(storagePrefix) &&
        window.sessionStorage.getItem(key) === sessionId
      ) {
        return true;
      }
    }
  } catch {
    // Storage unreadable: memory above is all this tab has.
  }
  return false;
};

/**
 * Drop the binding for real — the module cache AND the storage mirror, plus the
 * generation bump that stands down an in-flight mint.
 *
 * `clearPersistedSession` above deliberately leaves the cache alone, and the
 * cache is read FIRST — so a surface that self-heals with the storage-only
 * clear has not actually forgotten anything until a full reload. Use THIS where
 * the id is known unusable (`isSessionDead`) and the user may re-enter the
 * surface without reloading.
 *
 * Touches no React state on purpose: the mount that observes the death keeps
 * rendering its dead session (that is what puts the "this chat has ended"
 * screen on screen); only the NEXT mount cold-boots.
 */
export const clearProjectSessionBinding = (
  projectId: number,
  slot: SessionSlot = "issue",
  issueIid?: number | null,
): void => {
  dropBinding(projectId, bindingOf(slot, issueIid));
};

/**
 * The in-flight `POST /sessions` for a binding, if one is running right now.
 *
 * Exists for ONE caller: a surface unmounting inside the mint window. That
 * window is not small (GitLab round trips), and it has already triggered a real
 * CI pipeline by the time it resolves, while the surface's own `sessionId` is
 * still null and has nothing to detach. Joining the promise is the only way the
 * abandoning surface can name the session it caused.
 *
 * The promise resolves to the minted id whether or not the binding still wants
 * it, so the caller must re-check the binding before acting on it.
 */
export const pendingProjectSession = (
  projectId: number,
  slot: SessionSlot = "issue",
  issueIid?: number | null,
): Promise<string> | null =>
  inflight.get(memKey(projectId, bindingOf(slot, issueIid))) ?? null;

/**
 * Point a binding at a session the USER chose.
 *
 * The three mutations are distinct acts: `ensure()` mints or re-serves (it can
 * never change an existing binding), `reset()` clears, and this ADOPTS a
 * specific id. Writing `sessionStorage` directly does not work: the module cache
 * is checked first, so the write stays invisible until a reload.
 *
 * The caller owns the ordering around it — in particular DETACHING the outgoing
 * session before adopting, while its id is still readable here.
 */
export const adoptProjectSession = (
  projectId: number,
  sessionId: string,
  slot: SessionSlot = "issue",
  issueIid?: number | null,
): void => {
  const b = bindingOf(slot, issueIid);
  const key = memKey(projectId, b);
  writeBinding(projectId, b, sessionId, "adopted");
  inflight.delete(key);
  // Dropping the in-flight promise does not neuter the `.then` already attached
  // to it; the generation bump is what makes that handler stand down.
  bumpGeneration(key);
};

/**
 * Test seam — bind a known session. For the issue slot with no issue named it
 * clears every issue binding of the project first and makes `sessionId` the
 * pre-warm, so the next issue surface claims it: "this project's issue work is
 * on `sessionId`". `null` clears.
 */
export const __setProjectSessionForTest = (
  projectId: number,
  sessionId: string | null,
  slot: SessionSlot = "issue",
  issueIid?: number | null,
): void => {
  const b = bindingOf(slot, issueIid);
  if (b.kind === "warm") {
    const prefix = `issue:${projectId}:`;
    for (const key of [...sessionCache.keys(), ...inflight.keys()]) {
      if (key.startsWith(prefix)) {
        sessionCache.delete(key);
        bindingOrigin.delete(key);
        inflight.delete(key);
        bumpGeneration(key);
      }
    }
    try {
      const storagePrefix = `${ISSUE_BINDING_PREFIX}${projectId}:`;
      const doomed: string[] = [];
      for (let i = 0; i < window.sessionStorage.length; i += 1) {
        const key = window.sessionStorage.key(i);
        if (key !== null && key.startsWith(storagePrefix)) doomed.push(key);
      }
      for (const key of doomed) window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
    removeStorage(legacyStorageKey(projectId));
  } else {
    dropBinding(projectId, b);
  }
  if (sessionId !== null) {
    writeBinding(projectId, b, sessionId, "minted");
    bindingOrigin.delete(memKey(projectId, b));
  }
};

/** Test seam — forget every binding this module holds (a fresh tab's module). */
export const __resetProjectSessionsForTest = (): void => {
  sessionCache.clear();
  inflight.clear();
  bindingOrigin.clear();
  bindingGeneration.clear();
  legacyMigrations.clear();
};

export interface ProjectSessionState {
  /** The bound session id, or null until `ensure()` resolves. */
  readonly sessionId: string | null;
  /** True while a mint (or the one-time migration read) is in flight. */
  readonly starting: boolean;
  readonly error: string | null;
  /**
   * True when the last `ensure()` failure was a 403 — the signed-in user
   * lacks the Developer+ access the sandbox pre-flight requires on this
   * GitLab project. Distinguished from other `error` causes (503 membership
   * check, a transient 500) so a caller can show "you don't have access"
   * instead of a generic boot failure — those other causes are retryable,
   * this one isn't until someone changes the user's project role.
   *
   * Optional (rather than required) so the many existing test doubles across
   * the codebase that stub this hook's shape without it keep compiling —
   * every real caller treats a missing value the same as `false`.
   */
  readonly accessDenied?: boolean;
  /** Boot (or adopt the bound) session for this binding. Idempotent. */
  readonly ensure: () => void;
  /**
   * Evict this binding. Used when the live session is dead (expired /
   * terminated) so the next `ensure()` cold-boots a fresh one instead of
   * re-adopting the dead id (the expiry-recovery trap).
   */
  readonly reset: () => void;
}

/**
 * The session for one binding: `slot` "chat", or `slot` "issue" with the issue
 * the surface is on (`issueIid`), or with none for the pre-warm.
 */
export const useProjectSession = (
  projectId: number,
  slot: SessionSlot = "issue",
  issueIid?: number | null,
): ProjectSessionState => {
  const iid = slot === "issue" && typeof issueIid === "number" ? issueIid : null;
  const [sessionId, setSessionId] = useState<string | null>(() =>
    initialId(projectId, bindingOf(slot, iid)),
  );
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  // The binding this hook is on NOW. An `ensure()` that settles after the hook
  // moved to another binding writes nothing into its state (see `ensure`).
  const boundKeyRef = useRef(memKey(projectId, bindingOf(slot, iid)));

  // Re-read the binding when the project, slot or issue changes (the issue page
  // keeps its instance across /issues/208 -> /issues/209).
  useEffect(() => {
    boundKeyRef.current = memKey(projectId, bindingOf(slot, iid));
    setSessionId(initialId(projectId, bindingOf(slot, iid)));
    setStarting(false);
    setError(null);
    setAccessDenied(false);
  }, [projectId, slot, iid]);

  const ensure = useCallback(() => {
    const b = bindingOf(slot, iid);
    const key = memKey(projectId, b);
    // The older bundle's binding has to be read by the server before anything
    // is handed out; otherwise the answer is known now.
    const mustMigrate = b.kind !== "chat" && hasLegacyWork(projectId);
    if (!mustMigrate) {
      const now = resolveNow(projectId, b);
      if (now !== null) {
        traceBind(now.id, now.source, projectId, slot, b);
        setSessionId(now.id);
        return;
      }
    }
    // Captured BEFORE the mint starts, and re-read when it lands.
    const generation = generationOf(key);
    // Is the hook still on the binding this call started for? The issue page
    // keeps ONE instance across an in-place issue link (/issues/209 ->
    // /issues/210), and the generation is per binding, so it cannot see the
    // hook move. Without this, #209's mint landing on #210's page became the
    // sessionId there, and "Start refining" on #210 mounted ChatPage on #209's
    // session with #210's seed. The binding itself is still written by the
    // mint (`mintInto`), so #209 finds its session again on Back.
    const stillBound = (): boolean => boundKeyRef.current === key;
    setStarting(true);
    setError(null);
    setAccessDenied(false);
    const run = mustMigrate
      ? migrateLegacy(projectId).then(() => settle(projectId, b, generation))
      : settle(projectId, b, generation);
    run
      .then(({ id, source }) => {
        // Same guard on the hook state: the surface that owns this binding is
        // remounted by a pick, and adopting the superseded id here would put
        // the user back on the session they just stepped off.
        if (generationOf(key) !== generation) return;
        if (!stillBound()) return;
        traceBind(id, source, projectId, slot, b);
        setSessionId(id);
      })
      .catch((err: unknown) => {
        if (!stillBound()) return;
        // `POST /sessions` 403s with `insufficient_access` when the user is
        // below Developer on this GitLab project (apps/server/src/http/sessions.ts)
        // and never creates a session row — nothing will ever come up for
        // this project until someone changes that membership, so callers need
        // to tell this apart from a retryable boot failure.
        setAccessDenied(err instanceof HttpError && err.status === 403);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        // The binding the hook is on now may have a mint of its own running;
        // its `starting` is not this call's to clear.
        if (stillBound()) setStarting(false);
      });
  }, [projectId, slot, iid]);

  const reset = useCallback(() => {
    dropBinding(projectId, bindingOf(slot, iid));
    setSessionId(null);
    setStarting(false);
    setError(null);
    setAccessDenied(false);
  }, [projectId, slot, iid]);

  return { sessionId, starting, error, accessDenied, ensure, reset };
};
